// router/index.js
import { createRouter, createWebHistory } from "vue-router";
import MyFileList from "@/views/files/MyFileList.vue";
import Profile from "@/views/account/Profile.vue";
import { initializeMsal } from "@/plugins/msal";
import UploadFile from "@/views/upload/UploadFile.vue";
import ContactList from "@/views/contacts/ContactList.vue";
import ReceivedFileView from "@/views/received/ReceivedFileView.vue";
import InviteView from "@/views/invite/InviteView.vue";
import Feedback from "@/views/feedback/Feedback.vue";
import SharedFileList from "@/views/files/SharedFileList.vue";
import LandingPage from "@/views/LandingPage.vue";
import UnlockEncryption from "@/views/UnlockEncryption.vue";
import store from '@/store';
import { cryptoService } from '@/services/crypto.service';

const routes = [
  {
    path: "/",
    name: "/",
    redirect: "/new",
    meta: { requiresAuth: true },
  },
  {
    path: "/landing",
    name: "Landing",
    component: LandingPage,
    meta: { requiresAuth: false },
  },
  {
    path: "/contacts",
    name: "Contacts",
    component: ContactList,
    meta: { requiresAuth: true },
  },
  {
    path: "/new",
    name: "New Upload",
    component: UploadFile,
    meta: { requiresAuth: true },
  },
  {
    path: "/myfiles",
    name: "My Files",
    component: MyFileList,
    meta: { requiresAuth: true },
  },
  {
    path: "/sharedwithme",
    name: "Shared With Me",
    component: SharedFileList,
    meta: { requiresAuth: true },
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: { requiresAuth: true },
  },
  {
    path: "/unlock",
    name: "UnlockEncryption",
    component: UnlockEncryption,
    meta: { requiresAuth: true },
    async beforeEnter(to, from, next) {
      if (sessionStorage.getItem('redirectAfterUnlock')) {
        next();
        return;
      }
      next();
    }
  },
  {
    path: "/share/:id",
    name: "ReceivedFileView",
    component: ReceivedFileView,
    meta: { requiresAuth: true },
    async beforeEnter(to, from, next) {
      console.log('[Router:/share/:id] Entered beforeEnter');
      console.log('[Router:/share/:id] Route params:', to.params);
      try {
        const instance = await initializeMsal();
        console.log('[Router:/share/:id] MSAL instance:', instance);
        const accounts = instance.getAllAccounts();
        console.log('[Router:/share/:id] Accounts:', accounts);
        if (accounts.length === 0) {
          console.log('[Router:/share/:id] No accounts found, redirecting to /landing with reason=file-share and fileId:', to.params.id);
          next({
            path: '/landing',
            query: {
              reason: 'file-share',
              fileId: to.params.id,
            }
          });
        } else {
          console.log('[Router:/share/:id] Accounts found, proceeding');
          next();
        }
      } catch (error) {
        console.error('[Router:/share/:id] Error in beforeEnter:', error);
        next('/landing');
      }
    }
  },
  {
    path: "/invite/:id",
    name: "Invite",
    component:InviteView,
    meta: { requiresAuth: true },
    async beforeEnter(to, from, next) {
      console.log('[Router:/invite/:id] Entered beforeEnter');
      console.log('[Router:/invite/:id] Route params:', to.params);
      try {
        const instance = await initializeMsal();
        console.log('[Router:/invite/:id] MSAL instance:', instance);
        const accounts = instance.getAllAccounts();
        console.log('[Router:/invite/:id] Accounts:', accounts);
        if (accounts.length === 0) {
          console.log('[Router:/invite/:id] No accounts found, redirecting to /landing with reason=invite-email and fileId:', to.params.id);
          next({
            path: '/landing',
            query: {
              reason: 'invite-email',
              fileId: to.params.id,
            }
          });
        } else {
          console.log('[Router:/invite/:id] Accounts found, proceeding');
          next();
        }
      } catch (error) {
        console.error('[Router:/invite/:id] Error in beforeEnter:', error);
        next('/landing');
      }
    }
  },
  // {
  //   path: "/invite/:id",
  //   name: "Invite",
  //   meta: { requiresAuth: true },
  //   async beforeEnter(to, from, next) {
  //     console.log('[Router:/invite/:id] Entered beforeEnter');
  //     console.log('[Router:/invite/:id] Route params:', to.params);
  //     try {
  //       const instance = await initializeMsal();
  //       console.log('[Router:/invite/:id] MSAL instance:', instance);
  //       const accounts = instance.getAllAccounts();
  //       console.log('[Router:/invite/:id] Accounts:', accounts);
  //       if (accounts.length === 0) {
  //         console.log('[Router:/invite/:id] No accounts found, redirecting to /landing with reason=invite and fileId:', to.params.id);
  //         next({
  //           path: '/landing',
  //           query: {
  //             reason: 'invite',
  //             fileId: to.params.id
  //           }
  //         });
  //       } else {
  //         console.log('[Router:/invite/:id] Accounts found, redirecting to /new');
  //         next('/new');
  //       }
  //     } catch (error) {
  //       console.error('[Router:/invite/:id] Error in beforeEnter:', error);
  //       next('/landing');
  //     }
  //   }
  // },
  {
    path: "/feedback",
    name: "Feedback",
    component: Feedback,
    meta: { requiresAuth: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { requiresAuth: false }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

router.beforeEach(async (to, from, next) => {
  try {
    const instance = await initializeMsal();
    if (!instance) {
      // console.log('[Router] MSAL instance not initialized');
      next(false);
      return;
    }

    const accounts = instance.getAllAccounts();
    const interactionInProgress = sessionStorage.getItem("msal.interaction.status") === "interaction_in_progress";
    let isEncryptionInitialized = store.getters['encryption/isEncryptionInitialized'];
    let areKeysReady = cryptoService.areKeysReady();

    // console.log('[Router] Navigation:', { to: to.path, from: from.path });
    // console.log('[Router] Accounts:', accounts);
    // console.log('[Router] isEncryptionInitialized:', isEncryptionInitialized);
    // console.log('[Router] areKeysReady:', areKeysReady);

    // Check for file operations in progress
    const isFileOperationInProgress = store.getters['files/isFileOperationInProgress'];
    // console.log('[Router] isFileOperationInProgress:', isFileOperationInProgress);
    
    if (isFileOperationInProgress && to.path !== from.path) {
      const confirmed = window.confirm(
        'You have file operations in progress. Are you sure you want to leave? This may cancel your upload or download.'
      );
      
      if (confirmed) {
        // console.log('[Router] User confirmed navigation away from file operation');
        
        // Abort the current operation
        const isUploadInProgress = store.getters['files/isLoadingUpload'];
        const isDownloadInProgress = store.getters['files/isLoadingDownload'];
        
        // console.log('[Router] isUploadInProgress:', isUploadInProgress);
        // console.log('[Router] isDownloadInProgress:', isDownloadInProgress);
        
        if (isUploadInProgress) {
          // console.log('[Router] Aborting upload due to navigation');
          await store.dispatch('files/abortUploadAction');
          // Wait for state to be updated
          await new Promise(resolve => setTimeout(resolve, 200));
        } else if (isDownloadInProgress) {
          // console.log('[Router] Aborting download due to navigation');
          await store.dispatch('files/abortDownloadAction');
          // Wait for state to be updated
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        // Check state after abort with retry
        let isFileOperationInProgressAfter = store.getters['files/isFileOperationInProgress'];
        // console.log('[Router] isFileOperationInProgress after abort:', isFileOperationInProgressAfter);
        
        // If state is still showing operations in progress, wait a bit more
        if (isFileOperationInProgressAfter) {
          // console.log('[Router] State still shows operations in progress, waiting...');
          await new Promise(resolve => setTimeout(resolve, 300));
          isFileOperationInProgressAfter = store.getters['files/isFileOperationInProgress'];
          // console.log('[Router] isFileOperationInProgress after additional wait:', isFileOperationInProgressAfter);
        }
      } else {
        // console.log('[Router] User cancelled navigation due to file operation in progress');
        next(false);
        return;
      }
    }

    if (to.path !== '/unlock' && to.path !== '/landing') {
      sessionStorage.setItem('redirectAfterUnlock', to.fullPath);
    }

    if (to.matched.some(record => record.meta.requiresAuth)) {
      if (!interactionInProgress && accounts.length === 0) {
        const redirectPath = {
          path: '/landing',
          query: to.name === 'ReceivedFileView' && to.params.id ? {
            reason: 'file-share',
            fileId: to.params.id
          } : to.name === 'Invite' && to.params.id ? {
            reason: 'invite-email',
            fileId: to.params.id
          } : {}
        };
      // console.log('[Router] Redirecting to /landing due to no accounts');
        next(redirectPath);
        return;
      }
    }

    if (accounts.length > 0 && to.path !== '/landing') {
      // Try to restore encryption state if possible
      if ((!isEncryptionInitialized || !areKeysReady) && sessionStorage.getItem('encryptionDerivedKey')) {
        // console.log('[Router] Attempting to restore encryption from session...');
        try {
          await store.dispatch('encryption/restoreEncryptionFromSession');
          isEncryptionInitialized = store.getters['encryption/isEncryptionInitialized'];
          areKeysReady = cryptoService.areKeysReady();
          // console.log('[Router] After restore: isEncryptionInitialized:', isEncryptionInitialized, 'areKeysReady:', areKeysReady);
        } catch (error) {
          console.warn('[Router] Failed to restore encryption from session:', error);
          // Clear the derived key if restoration failed
          sessionStorage.removeItem('encryptionDerivedKey');
        }
      }
      if (!isEncryptionInitialized || !areKeysReady) {
        if (to.path !== '/unlock') {
          // console.log('[Router] Redirecting to /unlock due to encryption not ready');
          next('/unlock');
          return;
        }
      }
    }

    if (to.path === '/unlock' && accounts.length > 0 && isEncryptionInitialized && areKeysReady) {
      const redirectAfterUnlock = sessionStorage.getItem('redirectAfterUnlock');
      sessionStorage.removeItem('redirectAfterUnlock');
      const redirectPath = redirectAfterUnlock || '/new';
      // console.log('[Router] Redirecting from /unlock to', redirectPath);
      next(redirectPath);
      return;
    }

    // console.log('[Router] Proceeding to', to.path);
    next();
  } catch (error) {
    console.error('[Router] Navigation error:', error);
    // If there's an error during navigation, try to redirect to landing page
    if (to.path !== '/landing') {
      next('/landing');
    } else {
      next(false);
    }
  }
});

export default router;
